import React from 'react';


import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {Delete as DeleteIcon, PersonAdd as PersonAddIcon} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {Types, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {
  ConfirmationDialog,
  CircleLoader,
  LoadError,
  Head,
  MemberPanel,
  SearchForm,
  TooltipButton,useErrorHandler
} from '@wheeebo/polaris-ui';

import LoginEventsCard from '@components/pages/member/LoginEventsCard';

const INITIAL_ITEM_COUNT = 25;

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.admin.MemberListPage.header.title',
    defaultMessage: 'Member List',
  },
  headerSubTitle: {
    id: 'pages.admin.MemberListPage.header.subTitle',
    defaultMessage: 'Members registed on the polaris website.',
  },
  cellName: {
    id: 'pages.admin.MemberListPage.cell.name',
    defaultMessage: 'Name',
  },
  cellEmail: {
    id: 'pages.admin.MemberListPage.cell.email',
    defaultMessage: 'Email',
  },
  cellRole: {
    id: 'pages.admin.MemberListPage.cell.role',
    defaultMessage: 'Role',
  },
  cellJoinedAt: {
    id: 'pages.admin.MemberListPage.cell.joinedAt',
    defaultMessage: 'Joined',
  },
  errorNoMember: {
    id: 'pages.admin.MemberListPage.error.noMember',
    defaultMessage: 'member not found',
  },
  dialogConfirmationTitle: {
    id: 'pages.admin.MemberListPage.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.admin.MemberListPage.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this member?',
  },
  successMemberDeleted: {
    id: 'pages.admin.MemberListPage.success.memberDeleted',
    defaultMessage: 'Member deleted',
  },
  inviteButton: {
    id: 'pages.admin.MemberListPage.invite.button',
    defaultMessage: 'Add member',
  },
  inviteDialogTitle: {
    id: 'pages.admin.MemberListPage.invite.dialogTitle',
    defaultMessage: 'Invite member',
  },
  inviteDialogDescription: {
    id: 'pages.admin.MemberListPage.invite.dialogDescription',
    defaultMessage: 'A password-setup link will be emailed to the new member.',
  },
  inviteFieldName: {
    id: 'pages.admin.MemberListPage.invite.fieldName',
    defaultMessage: 'Name',
  },
  inviteFieldEmail: {
    id: 'pages.admin.MemberListPage.invite.fieldEmail',
    defaultMessage: 'Email',
  },
  inviteFieldRole: {
    id: 'pages.admin.MemberListPage.invite.fieldRole',
    defaultMessage: 'Role',
  },
  inviteSubmit: {
    id: 'pages.admin.MemberListPage.invite.submit',
    defaultMessage: 'Send invitation',
  },
  successMemberInvited: {
    id: 'pages.admin.MemberListPage.success.memberInvited',
    defaultMessage: 'Invitation sent',
  },
});

function formatJoinedAt(ms?: number): string {
  if (!ms) return '-';
  return new Date(ms).toLocaleDateString();
}

interface RowProps {
  member: Types.Member;
  onDelete: (memberId: Types.UUID) => void;
}

function Row({member, onDelete}: RowProps) {
  const navigate = useNavigate();
  return (
    <TableRow hover sx={{cursor: 'pointer'}} onClick={() => navigate(`/members/${member.id}`)}>
      <TableCell sx={{pl: 1 / 4}}>
        <MemberPanel member={member} />
      </TableCell>
      <TableCell>{member.email || '-'}</TableCell>
      <TableCell>{member.role || '-'}</TableCell>
      <TableCell>{formatJoinedAt(member.joinedAt)}</TableCell>
      <TableCell align="right">
        <TooltipButton
          message="Delete"
          icon={<DeleteIcon fontSize="small" />}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onDelete(member.id);
          }}
        />
      </TableCell>
    </TableRow>
  );
}

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (props: API.Members.InviteRequest) => void;
}

function InviteDialog({open, onClose, onSubmit}: InviteDialogProps): JSX.Element {
  const intl = useIntl();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<Types.MemberRole>(Types.MemberRoleMember);
  React.useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setRole(Types.MemberRoleMember);
    }
  }, [open]);
  const valid = name.trim().length > 0 && /\S+@\S+\.\S+/.test(email.trim());
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{intl.formatMessage(msgs.inviteDialogTitle)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{mb: 2}}>
          {intl.formatMessage(msgs.inviteDialogDescription)}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={intl.formatMessage(msgs.inviteFieldName)}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        />
        <TextField
          fullWidth
          margin="dense"
          type="email"
          label={intl.formatMessage(msgs.inviteFieldEmail)}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          label={intl.formatMessage(msgs.inviteFieldRole)}
          value={role}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRole(e.target.value as Types.MemberRole)}
        >
          {Types.MemberRoles.map((r: Types.MemberRole) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onSubmit({name: name.trim(), email: email.trim(), role})}
        >
          {intl.formatMessage(msgs.inviteSubmit)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberListPage(): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(0);
  const [selectedMemberId, setSelectedMemberId] = React.useState<Types.UUID>();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [query, setQuery] = React.useState<string>();
  const options: Types.PaginationProps = {
    limit: INITIAL_ITEM_COUNT,
    offset: page * INITIAL_ITEM_COUNT,
    query,
  };
  const {data, isError, isLoading} = useQuery(API.Members.listKey(options), () => API.Members.list(options));
  const handleDelete = (memberId: Types.UUID) => {
    setSelectedMemberId(memberId);
    setDeleteDialogOpen(true);
  };
  const {mutate: deleteMember} = useMutation<unknown, unknown>(
    () => {
      setDeleteDialogOpen(false);
      if (!selectedMemberId) {
        showError(new Error(intl.formatMessage(msgs.errorNoMember)));
        return Promise.reject({});
      }
      return API.Members.delete_(csrfToken, selectedMemberId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.Members.CacheKey);
        showSuccess(intl.formatMessage(msgs.successMemberDeleted));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: inviteMember} = useMutation<unknown, unknown, API.Members.InviteRequest>(
    (props: API.Members.InviteRequest) => API.Members.invite(csrfToken, props),
    {
      onSuccess: async () => {
        setInviteDialogOpen(false);
        queryClient.invalidateQueries(API.Members.CacheKey);
        showSuccess(intl.formatMessage(msgs.successMemberInvited));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Grid container spacing={1}>
      <Head title="Members" />
      <Grid item xs={12} lg={8}>
        <Card>
          <CardHeader
            title={intl.formatMessage(msgs.headerTitle)}
            subheader={intl.formatMessage(msgs.headerSubTitle)}
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
              >
                {intl.formatMessage(msgs.inviteButton)}
              </Button>
            }
          />
          <CardContent>
            <SearchForm onSearch={setQuery} />
            {isLoading ? <CircleLoader /> : null}
            {isError ? <LoadError /> : null}
            {data && data.total > 0 ? (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{intl.formatMessage(msgs.cellName)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellEmail)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellRole)}</TableCell>
                      <TableCell>{intl.formatMessage(msgs.cellJoinedAt)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.items?.map((e: Types.Member) => (
                      <Row key={e.id} member={e} onDelete={handleDelete} />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                        count={data?.total || 0}
                        rowsPerPage={INITIAL_ITEM_COUNT}
                        page={page}
                        SelectProps={{
                          inputProps: {'aria-label': 'rows per page'},
                        }}
                        onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                          setPage(newPage)
                        }
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2">No record found.</Typography>
            )}
          </CardContent>
        </Card>
        <ConfirmationDialog
          open={deleteDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogDeleteConfirmationContent)}
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={() => deleteMember()}
        />
        <InviteDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          onSubmit={(props: API.Members.InviteRequest) => inviteMember(props)}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <LoginEventsCard />
      </Grid>
    </Grid>
  );
}

export default React.memo(MemberListPage);
